/**
 * Forgot Password Page
 *
 * Password reset request flow with verification code entry.
 * Task 2.30.1 | Wave 2d Tier 8
 */

import { useState, useCallback } from 'react';

import { BrandWordmark } from '../components/shared/BrandWordmark';
import { PORTAL_BRAND } from '../config/branding';
import { forgotPassword, confirmForgotPassword } from '../lib/auth';

type Step = 'request' | 'verify' | 'success';

/**
 * Forgot Password component
 */
export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState<Step>('request');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRequestReset = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setIsLoading(true);

      try {
        await forgotPassword(email);
        setStep('verify');

        // Track event (GTM dataLayer)
        const dataLayer = (window as Window & { dataLayer?: unknown[] }).dataLayer;
        if (dataLayer) {
          dataLayer.push({
            event: 'password_reset_request',
            method: 'email',
          });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    },
    [email]
  );

  const handleConfirmReset = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      if (newPassword !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      if (newPassword.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }

      setIsLoading(true);

      try {
        await confirmForgotPassword(email, code, newPassword);
        setStep('success');

        // Track event
        const dataLayer = (window as Window & { dataLayer?: unknown[] }).dataLayer;
        if (dataLayer) {
          dataLayer.push({
            event: 'password_reset_complete',
            method: 'email',
          });
        }
      } catch (err) {
        const error = err as { name?: string; message?: string };
        if (error.name === 'CodeMismatchException') {
          setError('Invalid verification code. Please check and try again.');
        } else if (error.name === 'ExpiredCodeException') {
          setError('Verification code has expired. Please request a new one.');
        } else {
          setError(error.message ?? 'An error occurred');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [email, code, newPassword, confirmPassword]
  );

  // Success state
  if (step === 'success') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-4">
        <div className="mb-6 text-center">
          <a href={PORTAL_BRAND.websiteUrl}>
            <span
              className="text-2xl font-bold"
              style={{ color: 'hsl(var(--color-primary-400))' }}
            >
              <BrandWordmark />
            </span>
          </a>
          <a
            href={PORTAL_BRAND.websiteUrl}
            className="mt-1 block text-sm text-neutral-500 hover:text-neutral-300"
          >
            &larr; {PORTAL_BRAND.websiteLabel}
          </a>
        </div>
        <div
          className="w-full max-w-md rounded-lg border p-8 text-center"
          style={{
            backgroundColor: 'hsl(var(--color-neutral-100))',
            borderColor: 'hsl(var(--color-neutral-200))',
          }}
        >
          <div
            className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
            style={{ backgroundColor: 'hsl(var(--color-success-400) / 0.2)' }}
          >
            <svg
              className="h-8 w-8"
              style={{ color: 'hsl(var(--color-success-400))' }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold" style={{ color: 'hsl(var(--color-neutral-100))' }}>
            Password Reset Complete
          </h1>
          <p className="mt-2" style={{ color: 'hsl(var(--color-neutral-400))' }}>
            Your password has been successfully reset. You can now sign in with your new password.
          </p>
          <a
            href="/login"
            className="mt-6 inline-block rounded-md px-4 py-2 text-sm font-medium"
            style={{
              backgroundColor: 'hsl(var(--color-primary-500))',
              color: 'white',
            }}
          >
            Sign In
          </a>
        </div>
      </div>
    );
  }

  // Verify code and set new password
  if (step === 'verify') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-4">
        <div className="mb-6 text-center">
          <a href={PORTAL_BRAND.websiteUrl}>
            <span
              className="text-2xl font-bold"
              style={{ color: 'hsl(var(--color-primary-400))' }}
            >
              <BrandWordmark />
            </span>
          </a>
          <a
            href={PORTAL_BRAND.websiteUrl}
            className="mt-1 block text-sm text-neutral-500 hover:text-neutral-300"
          >
            &larr; {PORTAL_BRAND.websiteLabel}
          </a>
        </div>
        <div
          className="w-full max-w-md rounded-lg border p-8"
          style={{
            backgroundColor: 'hsl(var(--color-neutral-100))',
            borderColor: 'hsl(var(--color-neutral-200))',
          }}
        >
          <div className="text-center">
            <h1 className="text-2xl font-bold" style={{ color: 'hsl(var(--color-neutral-100))' }}>
              Reset Your Password
            </h1>
            <p className="mt-2" style={{ color: 'hsl(var(--color-neutral-400))' }}>
              Enter the verification code sent to <strong>{email}</strong>
            </p>
          </div>

          <form onSubmit={handleConfirmReset} className="mt-8 space-y-4">
            {error && (
              <div
                className="rounded-md p-3 text-sm"
                style={{
                  backgroundColor: 'hsl(var(--color-error-400) / 0.1)',
                  color: 'hsl(var(--color-error-400))',
                }}
              >
                {error}
              </div>
            )}

            <div>
              <label
                htmlFor="code"
                className="block text-sm font-medium"
                style={{ color: 'hsl(var(--color-neutral-300))' }}
              >
                Verification Code
              </label>
              <input
                id="code"
                name="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="mt-1 block w-full rounded-md border px-3 py-2 text-sm text-center tracking-widest focus:outline-hidden focus:ring-2"
                style={{
                  backgroundColor: 'hsl(var(--color-neutral-100))',
                  borderColor: 'hsl(var(--color-neutral-300))',
                  color: 'hsl(var(--color-neutral-100))',
                  fontSize: '1.25rem',
                }}
                placeholder="000000"
                maxLength={6}
              />
            </div>

            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium"
                style={{ color: 'hsl(var(--color-neutral-300))' }}
              >
                New Password
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border px-3 py-2 text-sm focus:outline-hidden focus:ring-2"
                style={{
                  backgroundColor: 'hsl(var(--color-neutral-100))',
                  borderColor: 'hsl(var(--color-neutral-300))',
                  color: 'hsl(var(--color-neutral-100))',
                }}
                placeholder="At least 8 characters"
              />
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium"
                style={{ color: 'hsl(var(--color-neutral-300))' }}
              >
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border px-3 py-2 text-sm focus:outline-hidden focus:ring-2"
                style={{
                  backgroundColor: 'hsl(var(--color-neutral-100))',
                  borderColor: 'hsl(var(--color-neutral-300))',
                  color: 'hsl(var(--color-neutral-100))',
                }}
                placeholder="Confirm your password"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
              style={{
                backgroundColor: 'hsl(var(--color-primary-500))',
                color: 'white',
              }}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Resetting...
                </span>
              ) : (
                'Reset Password'
              )}
            </button>

            <div className="text-center text-sm" style={{ color: 'hsl(var(--color-neutral-500))' }}>
              Didn't receive a code?{' '}
              <button
                type="button"
                onClick={() => {
                  setStep('request');
                  setCode('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setError(null);
                }}
                className="underline"
                style={{ color: 'hsl(var(--color-primary-400))' }}
              >
                Try again
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4">
      <div className="mb-6 text-center">
        <a href={PORTAL_BRAND.websiteUrl}>
          <span
            className="text-2xl font-bold"
            style={{ color: 'hsl(var(--color-primary-400))' }}
          >
            <BrandWordmark />
          </span>
        </a>
        <a
          href={PORTAL_BRAND.websiteUrl}
          className="mt-1 block text-sm text-neutral-500 hover:text-neutral-300"
        >
          &larr; {PORTAL_BRAND.websiteLabel}
        </a>
      </div>
      <div
        className="w-full max-w-md rounded-lg border p-8"
        style={{
          backgroundColor: 'hsl(var(--color-neutral-100))',
          borderColor: 'hsl(var(--color-neutral-200))',
        }}
      >
        <div className="text-center">
          <h1 className="text-2xl font-bold" style={{ color: 'hsl(var(--color-neutral-100))' }}>
            Forgot Password
          </h1>
          <p className="mt-2" style={{ color: 'hsl(var(--color-neutral-400))' }}>
            Enter your email and we'll send you a reset link
          </p>
        </div>

        <form onSubmit={handleRequestReset} className="mt-8 space-y-6">
          {error && (
            <div
              className="rounded-md p-3 text-sm"
              style={{
                backgroundColor: 'hsl(var(--color-error-400) / 0.1)',
                color: 'hsl(var(--color-error-400))',
              }}
            >
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium"
              style={{ color: 'hsl(var(--color-neutral-300))' }}
            >
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full rounded-md border px-3 py-2 text-sm focus:outline-hidden focus:ring-2"
              style={{
                backgroundColor: 'hsl(var(--color-neutral-100))',
                borderColor: 'hsl(var(--color-neutral-300))',
                color: 'hsl(var(--color-neutral-100))',
              }}
              placeholder="you@example.com"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
            style={{
              backgroundColor: 'hsl(var(--color-primary-500))',
              color: 'white',
            }}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Sending...
              </span>
            ) : (
              'Send Reset Link'
            )}
          </button>

          <div className="text-center">
            <a href="/login" className="text-sm" style={{ color: 'hsl(var(--color-primary-400))' }}>
              Back to login
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ForgotPassword;
